/**
 * BoardCreateModal — 게시판 생성 다이얼로그
 *
 * 게시판 이름, 설명, 스코프, 채널, 유형, 디자인 스타일을 선택하여 생성.
 * 스코프별 허용 유형 자동 필터링 (ALLOWED_TYPES_BY_SCOPE).
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AdminBadge } from '../components/AdminBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { createBoard, updateBoard } from '../../services/BoardService';
import { My } from '../../services/My';
import {
  BoardType, BoardScope, DisplayStyle, WritePermission, CommentPermission, MinRole,
  NotifyPermission,
  ALLOWED_TYPES_BY_SCOPE, DEFAULT_STYLE_BY_TYPE,
  WRITE_PERMISSIONS, COMMENT_PERMISSIONS, MIN_ROLES, NOTIFY_PERMISSIONS,
  getWritePermissionOption, getCommentPermissionOption, getMinRoleOption, getNotifyPermissionOption,
} from '../../models/Board';
import { useBoardContext } from '../../context/BoardContext';
import { useAppUIContext } from '../../context/AppUIContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/use-toast';
import {
  FileText, Columns3, BookOpen, ListTodo,
  ChevronDown, Shield, ShieldCheck,
} from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 사이드바에서 스코프를 미리 지정하여 열 때 */
  initialScope?: BoardScope;
  initialChannelId?: string;
}

// 라벨/desc 는 i18n 적용 — 사용처(useMemo)에서 t`...` 로 평가.
// type 'issue' / 'qna' 는 신규 생성 UI 옵션에서 숨김 (ALLOWED_TYPES_BY_SCOPE 에서도 제외).
// 기존 게시판은 BoardEditModal 의 useAllBoardTypes + board.type 보호로 안전.
function useBoardTypes(): { value: BoardType; label: string; desc: string; icon: React.ElementType }[] {
  return [
    { value: 'general', label: t`General`,    desc: t`Free post sharing`,      icon: FileText },
    { value: 'notice',  label: t`Notice`,     desc: t`Important announcements`,icon: FileText },
    { value: 'wiki',    label: t`Wiki`,       desc: t`Document organization`,  icon: BookOpen },
  ];
}

// 보기 스타일은 관리자 설정 영역(관리자만 변경 가능 — 일단 비표시) 으로 이관.
// 생성 모달에서는 type 별 DEFAULT_STYLE_BY_TYPE 자동 적용 — 사용자 선택 UI 제거.
// const DISPLAY_STYLES: { value: DisplayStyle; ... }[] = [
//   { value: 'notion',    label: '데이터베이스/테이블', ... },
//   { value: 'basecamp',  label: '카드형 (그리드)',    ... },
//   { value: 'discourse', label: '포럼형',             ... },
// ];

const BoardCreateModal: React.FC<Props> = ({
  open, onOpenChange, initialScope, initialChannelId,
}) => {
  const { refreshBoards, setSelectedBoard } = useBoardContext();
  const { activeWorkspaceId, rooms } = useAppUIContext();
  // 워크스페이스 정책의 기본값 — 모달 열릴 때마다 최신값으로 반영
  const wsSettings = useWorkspaceSettings();
  const defs = wsSettings.board_defaults;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<BoardScope>(initialScope ?? 'workspace');
  const [channelId, setChannelId] = useState<string>(initialChannelId ?? '');
  const [type, setType] = useState<BoardType>('general');
  const [displayStyle, setDisplayStyle] = useState<DisplayStyle>(DEFAULT_STYLE_BY_TYPE['general']);
  const [writePermission, setWritePermission] = useState<WritePermission>(defs.write_permission);
  const [commentPermission, setCommentPermission] = useState<CommentPermission>(defs.comment_permission);
  const [notifyAllPermission, setNotifyAllPermission] = useState<NotifyPermission>(defs.notify_all_permission);
  const [minRole, setMinRole] = useState<MinRole>('member');
  const [adminIds, setAdminIds] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때마다 워크스페이스 정책 기본값으로 권한 reset
  // (정책 변경 직후 모달 다시 열면 새 default 반영)
  useEffect(() => {
    if (!open) return;
    setWritePermission(defs.write_permission);
    setCommentPermission(defs.comment_permission);
    setNotifyAllPermission(defs.notify_all_permission);
  }, [open]);

  // 채널 게시판일 때 관리자 선택 대상 — 선택된 채널의 멤버들
  const channelMembers = useMemo(() => {
    if (scope !== 'channel' || !channelId) return [];
    const room = rooms.find(r => r.id === channelId);
    return room?.users ?? [];
  }, [scope, channelId, rooms]);

  const allBoardTypes = useBoardTypes();
  // 스코프별 허용 유형 필터
  const allowedTypes = useMemo(() => {
    return allBoardTypes.filter(bt =>
      ALLOWED_TYPES_BY_SCOPE[scope].includes(bt.value)
    );
  }, [scope, allBoardTypes]);

  // Dialog 열릴 때 초기화
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setName('');
      setDescription('');
      setScope(initialScope ?? 'workspace');
      setChannelId(initialChannelId ?? '');
      setType('general');
      setDisplayStyle(DEFAULT_STYLE_BY_TYPE['general']);
      setWritePermission('members');
      setCommentPermission('everyone');
      setNotifyAllPermission('admin-only');
      setMinRole('member');
      setAdminIds([]);
      setShowAdvanced(false);
    }
    onOpenChange(open);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !activeWorkspaceId || submitting || !My.user) return;
    if (scope === 'channel' && !channelId) {
      toast({ title: t`Please select a channel`, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      // 채널 스코프는 권한이 "채널 멤버 누구나 작성/읽기 가능" 으로 고정
      const isChannel = scope === 'channel';
      const effectiveWritePerm = isChannel ? 'members' : writePermission;
      const effectiveCommentPerm = isChannel ? 'members' : commentPermission;
      const effectiveMinRole: MinRole = isChannel ? 'member' : minRole;
      const board = await createBoard({
        workspace_id: activeWorkspaceId,
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        scope,
        channel_id: isChannel ? channelId : undefined,
        owner_id: My.user.id,
        admin_ids: isChannel ? adminIds : undefined,
        write_permission: effectiveWritePerm,
        comment_permission: effectiveCommentPerm,
        notify_all_permission: notifyAllPermission,
        min_role: effectiveMinRole,
      });
      // display_style 업데이트 — type 별 DEFAULT 와 다르면 저장. (canonical: notion)
      const defaultStyle = DEFAULT_STYLE_BY_TYPE[type];
      if (displayStyle !== defaultStyle) {
        await updateBoard(board.id, { display_style: displayStyle });
      }
      handleOpenChange(false);
      await refreshBoards();
      setSelectedBoard(board);
      toast({ title: t`Board "${board.name}" created` });
    } catch (err) {
      console.error('[BoardCreate] 게시판 생성 실패:', err);
      toast({ title: t`Failed to create board`, description: (err as Error).message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[85vh] flex flex-col gap-0">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Trans>Create new board</Trans>
            <AdminBadge variant="sm" />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 mt-2 -mr-6 pr-6">
          {/* 이름 */}
          <Input
            placeholder={t`Board name`}
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />

          {/* 설명 */}
          <Input
            placeholder={t`Description (optional)`}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />

          {/* ── 유형 ── */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trans>Board type</Trans>
            </label>
            <Select value={type} onValueChange={v => {
              const newType = v as BoardType;
              setType(newType);
              setDisplayStyle(DEFAULT_STYLE_BY_TYPE[newType]);
            }}>
              <SelectTrigger>
                <SelectValue placeholder={t`Board type`} />
              </SelectTrigger>
              <SelectContent>
                {allowedTypes.map(bt => (
                  <SelectItem key={bt.value} value={bt.value}>
                    <div className="flex items-center gap-2">
                      <span>{bt.label}</span>
                      <span className="text-meta-sm text-muted-foreground">{bt.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── 디자인 스타일 — 관리자만 변경 가능, 일단 비표시 (type 별 DEFAULT_STYLE_BY_TYPE 자동 적용) ── */}

          {/* ── 채널 게시판 전용: 관리자 선택 (채널 멤버 중에서) ── */}
          {scope === 'channel' && channelId && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" />
                <Trans>Admins</Trans>
              </label>
              <p className="text-meta-sm text-muted-foreground -mt-1">
                <Trans>Any channel member can read/write. You can also designate admins.</Trans>
              </p>
              {channelMembers.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 rounded-lg">
                  <Trans>No channel members</Trans>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {channelMembers.map(u => {
                    const isMe = u.id === My.user?.id;
                    const checked = isMe || adminIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={cn(
                          'flex items-center gap-2.5 px-2.5 py-2 text-sm transition-colors',
                          isMe
                            ? 'opacity-70 cursor-not-allowed'
                            : 'hover:bg-muted/60 cursor-pointer',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isMe}
                          onChange={e => {
                            if (e.target.checked) {
                              setAdminIds(prev => Array.from(new Set([...prev, u.id])));
                            } else {
                              setAdminIds(prev => prev.filter(id => id !== u.id));
                            }
                          }}
                          className="size-4 rounded border-border"
                        />
                        <UserAvatar src={u.photoUrl} name={u.name} className="size-6" />
                        <span className="flex-1 truncate">{u.name}</span>
                        {isMe && (
                          <span className="text-meta-sm text-muted-foreground"><Trans>Creator (auto)</Trans></span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 고급 설정 (접기/펼치기) — 채널 스코프는 권한 고정이므로 숨김 ── */}
          {scope !== 'channel' && (
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
            >
              <Shield className="size-4" />
              <span><Trans>Permissions</Trans></span>
              <ChevronDown className={cn('size-3.5 ml-auto transition-transform duration-200', showAdvanced && 'rotate-180')} />
            </button>
            {showAdvanced && (
              <div className="px-3 pb-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200 border-t border-border pt-3">
                {/* 게시글 작성 권한 */}
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Post write</Trans></label>
                  <Select value={writePermission} onValueChange={v => setWritePermission(v as WritePermission)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WRITE_PERMISSIONS.map(k => {
                        const o = getWritePermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {/* 댓글 작성 권한 */}
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Comment write</Trans></label>
                  <Select value={commentPermission} onValueChange={v => setCommentPermission(v as CommentPermission)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMENT_PERMISSIONS.map(k => {
                        const o = getCommentPermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {/* @all 멘션 권한 — @개인 멘션은 작성권한과 자동 일치이므로 별도 노출 안 함 */}
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>@all mention</Trans></label>
                  <Select value={notifyAllPermission} onValueChange={v => setNotifyAllPermission(v as NotifyPermission)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTIFY_PERMISSIONS.map(k => {
                        const o = getNotifyPermissionOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {/* 게스트 접근 */}
                <div className="space-y-1.5">
                  <label className="text-meta-sm font-semibold text-muted-foreground"><Trans>Guest access</Trans></label>
                  <Select value={minRole} onValueChange={v => setMinRole(v as MinRole)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MIN_ROLES.map(k => {
                        const o = getMinRoleOption(k);
                        return (
                        <SelectItem key={k} value={k}>
                          <span>{o.label}</span>
                          <span className="text-meta-sm text-muted-foreground ml-1.5">{o.desc}</span>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ── 액션 ── */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || submitting}>
              {submitting ? <Trans>Creating…</Trans> : <Trans>Create</Trans>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoardCreateModal;
